import { convertAddonsData } from '../../utils/bookingHelpers';
import { findObjectByProperty } from '../../utils/helpers';
import { safeParse } from '../../utils/stringHelpers';
import * as type from '../actions/types';
import { bookingKeys, bookingSteps } from '../models/Booking';
import { ALL_SESSIONS_LABEL } from '../models/obeeSessions';

const DEFAULT_GUEST_SIZE = 2;

/**
 * The API returns defaultGuestSize as an array of one string, e.g. ["3"] (possibly JSON encoded),
 * and it is absent when unset. Anything else (e.g. a boolean) falls back to the default.
 */
const parseDefaultGuestSize = (raw) => {
  const parsed = typeof raw === 'string' ? safeParse(raw, raw) : raw;
  const value = Array.isArray(parsed) ? parsed[0] : parsed;
  const size = typeof value === 'boolean' ? NaN : Number(value);
  return Number.isInteger(size) && size > 0 ? size : DEFAULT_GUEST_SIZE;
};

const initialState = {
  [bookingKeys.fetchingExisting]: false, // fetching existing details from an email link

  step: bookingSteps.availability, // 0 = availability, 1 = details, 2 = review, 3 = confirm
  [bookingKeys.subStep]: null, // Open a specific thing on the step. e.g. specific addon or guest details
  editBooking: false,
  editWaitlist: false,

  [bookingKeys.date]: null,
  [bookingKeys.guests]: DEFAULT_GUEST_SIZE,
  [bookingKeys.highchairs]: 0,
  [bookingKeys.kids]: 0,
  [bookingKeys.areaId]: -1,
  [bookingKeys.sessionId]: -1,
  [bookingKeys.sessionLabel]: ALL_SESSIONS_LABEL,
  [bookingKeys.availabilitySuggestion]: null,
  [bookingKeys.preferredTime]: -1,
  [bookingKeys.preferredAreaId]: -1,

  [bookingKeys.firstName]: null,
  [bookingKeys.lastName]: null,
  [bookingKeys.mobile]: null,
  [bookingKeys.email]: null,
  [bookingKeys.specialRequirements]: null,
  [bookingKeys.mobilePrefix]: null,

  [bookingKeys.cardName]: null,
  [bookingKeys.paymentMethodId]: null,
  [bookingKeys.paymentMethodName]: null,
  [bookingKeys.setupIntentId]: null,

  [bookingKeys.initialRestId]: null,
  [bookingKeys.heldBookingId]: null,

  [bookingKeys.selectedExperiences]: [],
  [bookingKeys.addons]: {},
  [bookingKeys.hasHeldRooms]: false,
  [bookingKeys.dietaryRequirements]: [],
  [bookingKeys.voucherReferences]: [],
  [bookingKeys.customBookingFieldValues]: {},
};

/**
 * The booking state as it is being created / edited
 * @param state
 * @param action
 * */
export default (state = initialState, action) => {
  switch (action.type) {
    case type.CLEAR_BOOKING: {
      return initialState;
    }

    case type.EDIT_BOOKING: {
      return { ...state, step: bookingSteps.availability, editBooking: true };
    }

    case type.EDIT_WAITLIST: {
      return { ...state, step: bookingSteps.availability, editWaitlist: true };
    }

    case type.REMOVE_ADDON: {
      const newAddons = { ...state.addons };
      delete newAddons[action.payload];

      return { ...state, addons: newAddons };
    }

    case type.UPDATE_BOOKING: {
      const { key, value } = action.payload;

      let newState = { ...state, [key]: value };

      // reset sessionId to -1 on an area or date change as areas and dates can have various different sessions
      if ([bookingKeys.areaId, bookingKeys.date].includes(key)) {
        newState = {
          ...newState,
          [bookingKeys.sessionId]: -1,
          [bookingKeys.sessionLabel]: ALL_SESSIONS_LABEL,
        };
      }

      // changes to the area, guest size or date should remove the current available suggestion selected
      if ([bookingKeys.areaId, bookingKeys.guests, bookingKeys.date].includes(key)) {
        newState = { ...newState, [bookingKeys.availabilitySuggestion]: null };
      }

      return newState;
    }

    case type.BATCH_UPDATE_BOOKING: {
      const newState = { ...state, ...action.payload };
      return newState;
    }

    case type.UPDATE_BOOKING_STEP: {
      return { ...state, step: action.payload.step, subStep: action.payload.subStep };
    }

    case type.UPDATE_BOOKING_SUB_STEP: {
      return { ...state, subStep: action.payload.subStep };
    }

    // note: restaurantForSlug is called for just the initial venue, let's store it for later
    case type.RESTAURANT_FOR_SLUG_SUCCESS: {
      return {
        ...state,
        [bookingKeys.initialRestId]: action.payload.data.objectId,
        [bookingKeys.guests]: parseDefaultGuestSize(
          safeParse(action.payload.data.bookingWidgetSettings)?.widgetSettings?.settingsDetails
            ?.defaultGuestSize,
        ),
      };
    }

    case type.HOLD_OBEE_BOOKING_SUCCESS: {
      const suggestedTable = state[bookingKeys.availabilitySuggestion]?.table;
      return {
        ...state,
        [bookingKeys.heldBookingId]: action.payload.data.id,
        [bookingKeys.areaId]: action.payload.data.areaId,
        // Holding can return a different table, so be ready to override the previous availability suggestion
        [bookingKeys.availabilitySuggestion]: {
          ...state[bookingKeys.availabilitySuggestion],
          table: {
            ...suggestedTable,
            id: action.payload.data?.tableId || suggestedTable?.id, // if holding returns a tableId, use it, otherwise keep the same suggested table
            areaId: action.payload.data.areaId,
            tableNumber: action.payload.data.tableNumber,
          },
        },
      };
    }

    case type.HOLD_OBEE_BOOKING_FAILURE: {
      // On failure, there was no valid table, so back to first page and re-fetch the suggestions
      return {
        ...state,
        [bookingKeys.heldBookingId]: null,
        // [bookingKeys.areaId]: null,
        step: 0,
        // Holding can return a different table, so be ready to override the previous availability suggestion
        [bookingKeys.availabilitySuggestion]: null,
      };
    }

    case type.FREE_HELD_OBEE_BOOKING_SUCCESS: {
      return {
        ...state,
        [bookingKeys.selectedExperiences]: [],
        [bookingKeys.addons]: {},
        [bookingKeys.heldBookingId]: null,
        [bookingKeys.hasHeldRooms]: false,
      };
    }

    // When the booking is fetched for editing, set the local state
    case type.FETCH_OBEE_BOOKING_SUCCESS: {
      const payments = safeParse(action.payload.data.payments)?.data || [];
      return {
        ...state,
        payments,
        addons: convertAddonsData(safeParse(action.payload.data?.addOns, [])),
        selectedExperiences: safeParse(action.payload.data?.experiences, [])?.map((experience) => {
          const purchasedExperiences = safeParse(action.payload.data?.purchasedExperiences, []);
          const purchasedExp = findObjectByProperty(
            purchasedExperiences,
            experience?.id,
            'experienceId',
          );

          return {
            id: experience?.id,
            count: purchasedExp?.claimedQuantity || 1, // TODO need API to return the count
            experience,
          };
        }),
        voucherReferences: safeParse(action.payload.data?.vouchersRedeemed, [])?.map((voucher) => ({
          voucher: voucher?.voucherReference,
          valid: true,
          amount: voucher?.redeemedAmount,
        })),
      };
    }

    // When the booking is fetched for editing, set the local state
    case type.HOLD_SELECTED_ROOMS_SUCCESS: {
      return {
        ...state,
        [bookingKeys.hasHeldRooms]: true,
      };
    }

    default: {
      return state;
    }
  }
};
